import React, { useState, useCallback } from 'react';
import axios from '../../api/api';
import { MdOutlineBlock } from 'react-icons/md';
import { FaSearch } from "react-icons/fa";
import Loader from '../Loader';

const Balance = () => {

  const [balance, setBalance] = useState([]);
  const [searchProvider, setSearchProvider] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Convert a date to partition format pYYYYMMDD
  const formatDateToPartition = (date) => {
    const d = new Date(date);
    return `p${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  };

  // Fetch balances
  const getBalances = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      let query = `&limit=5`

      if (!searchProvider) {
        setLoading(false);
        return;
      }

      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Check if endDate is before startDate
        if (end < start) {
          alert('End date cannot be earlier than start date');
          setLoading(false);
          return;
        }
        
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 10) {
          alert('Date range cannot exceed 10 days');
          setLoading(false);
          return;
        }

        const partitions = [];
        let current = new Date(startDate);
        
        // Handle case when start and end are the same
        if (diffDays === 0) {
          let current = new Date(startDate);
          current.setDate(current.getDate() + 1);
          partitions.push(formatDateToPartition(current));
        } else {
          while (current <= end) {
            current.setDate(current.getDate() + 1);
            partitions.push(formatDateToPartition(current));
          }
        }
        query += `&partitions=${partitions.join(',')}`;
      } else if (startDate) {
        const partitions = [];
        let current = new Date(startDate);
        current.setDate(current.getDate() + 1);
        partitions.push(formatDateToPartition(current));
        query += `&partitions=${partitions.join(',')}`;
      }

      const response = await axios.get(`/reports/balances?provider=${searchProvider}${query}`);
      setBalance(response.data.items);
      setLoading(false);
      setError(null);
    } catch (error) {
      console.log(error);
      setLoading(false);
      if (!error?.response?.status) {
        setError('Network error! Check your connection.');
      } else if (error.response.status === 400 || error.response.status === 404) {
        setError(error.response.data?.detail || 'Invalid request or resource not found');
      } else if (error.response.status === 500) {
        setError(error.response.data?.detail || 'Server error');
      } else {
        setError(error.response.data?.detail || 'Oops! Something went wrong! Try Again');
      }
    } finally {
      setLoading(false);
    }
  }, [searchProvider, startDate, endDate]);


  // Handle search button click
  const handleSearch = () => {
    setSearched(true);
    getBalances();
  };

  // Handle clearing search inputs
  const handleClearSearch = () => {
    setSearchProvider('');
    setStartDate('');
    setEndDate('');
    setBalance([]); 
    setSearched(false); 
  };

  

  return (
    <div className='mx-auto p-4'>
      <h3 className="my-2"><b>Balance</b></h3>
      <div className="bg-white rounded-lg p-4 w-full flex-1 min-w-0">
        <div className='flex flex-wrap gap-2 items-center mb-4'>
          <label htmlFor='search'><span className='hidden'>Search</span>
            <input
              type='text'
              id='search'
              className='px-3 py-1 w-full border bg-[#eef1ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#2b1e3e] focus:ring-[#2b1e3e] focus:ring-1'
              required
              placeholder='Search provider...'
              value={searchProvider}
              onChange={(e) => setSearchProvider(e.target.value)}
            />
          </label>
          <label htmlFor="">
              <span className='hidden'>Start Date</span>
              <input 
                type="date"
                className='px-2 py-1 border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#2b1e3e] focus:ring-[#2b1e3e] focus:ring-1'
                value={startDate}
                onChange={(e) => {setStartDate(e.target.value)}}
              />
            </label>
            <label htmlFor="">
              <span className='hidden'>End Date</span>
              <input 
                type="date" 
                className='px-2 py-1 border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#2b1e3e] focus:ring-[#2b1e3e] focus:ring-1'
                value={endDate}
                onChange={(e) => {setEndDate(e.target.value)}}
              />
            </label>
          <button
            onClick={handleSearch}
            className='px-4 py-1 bg-[#2b1e3e] text-white rounded-md'
          >
            Search
          </button>
          <button
            onClick={handleClearSearch}
            className='px-4 py-1 bg-red-500 text-white rounded-md'
          >
            Clear
          </button>
        </div>
        {loading ? (
          <div className='flex items-center justify-center bg-[#eef1ff] h-[70vh]'>
            <Loader />
          </div>
        ) : error ? (
          <div className='bg-[#eef1ff] grid place-items-center h-[70vh]'>
            <div className='grid place-items-center text-red-600 p-4'>
              <h3><MdOutlineBlock /></h3>
              <span>{error}</span>
            </div>
          </div>
        ) : (
          searched ? (
          <div className='overflow-x-auto rounded-lg'>
            {balance.length > 0
              ? (
                <table className='w-full text-left table-auto'>
                  <thead>
                    <tr className='border-b border-slate-500'>
                      <th className='p-2'>Provider</th>
                      <th className='p-2'>Response</th>
                      <th className='p-2 whitespace-nowrap'>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balance.map((bal) => (
                      <tr key={bal.id}>
                        <td className='p-2'>{bal.provider}</td>
                        <td className='p-2'>{bal.response}</td>
                        <td className='p-2 whitespace-nowrap'>{new Date(bal.date_created).toLocaleString("en-CA", { hour12: false })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className='bg-[#eef1ff] text-[#134463] grid place-items-center h-[70vh]'>
                  <div className='grid place-items-center p-4'>
                    <h3><MdOutlineBlock /></h3>
                    <h4>No Data</h4>
                  </div>
                </div>
              )}
          </div>
          ) : (
            <div className='grid place-items-center h-[70vh]'>
              <div className='text-[#134463] p-4'>
                <h3 className='flex items-center justify-center'><FaSearch /></h3>
                <h4>Search Provider Balance</h4>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Balance;